"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import * as React from "react";

import { cn } from "../lib/utils";

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

const DialogTrigger = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger>
>((props, ref) => {
  return <DialogPrimitive.Trigger ref={ref} data-slot="dialog-trigger" {...props} />;
});
DialogTrigger.displayName = "DialogTrigger";

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

const DialogClose = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Close
      ref={ref}
      data-slot="dialog-close"
      className={cn(
        "ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      <XIcon />
      <span className="sr-only">Close</span>
    </DialogPrimitive.Close>
  );
});
DialogClose.displayName = "DialogClose";

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
    disableScrollLock?: boolean;
    position?: "fixed" | "absolute";
  }
>(({ className, disableScrollLock = false, position = "fixed", ...props }, ref) => {
  const Comp = disableScrollLock ? "div" : DialogPrimitive.Overlay;

  return (
    <>
      {disableScrollLock && (
        <style>{`
  html, body {
    overflow: hidden;
  }
`}</style>
      )}
      <Comp
        ref={ref}
        data-slot="dialog-overlay"
        className={cn(
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 inset-0 z-200 bg-black/50",
          position === "absolute" ? "absolute" : "fixed",
          className
        )}
        {...props}
      />
    </>
  );
});
DialogOverlay.displayName = "DialogOverlay";

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    showCloseButton?: boolean;
    disableScrollLock?: boolean;
    overlayProps?: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>;
    portalContainer?: HTMLElement | null;
  }
>(
  (
    {
      className,
      children,
      showCloseButton = true,
      disableScrollLock = false,
      overlayProps,
      portalContainer,
      onOpenAutoFocus,
      onCloseAutoFocus,
      ...props
    },
    ref
  ) => {
    // Radix hands focus back to the `DialogTrigger` on close, and only to it: it cancels the focus
    // scope's own return. A dialog opened without a trigger — from a table row, a menu item, a
    // keyboard shortcut, most of this app's — had nothing to give focus back to, and it fell to
    // <body>. So the element that had focus when the dialog opened is remembered and given focus
    // back. When there is a trigger, that element is the trigger, so nothing changes for those.
    const openerRef = React.useRef<HTMLElement | null>(null);

    // If callers pass `portalContainer={...}`, they typically also run the dialog as non-modal.
    // Radix doesn't render `DialogPrimitive.Overlay` for non-modal dialogs, so we render a plain
    // overlay div in that case (scoped to the portal container when provided).
    const hasPortalContainerProp = portalContainer !== undefined;
    const position = hasPortalContainerProp ? "absolute" : "fixed";

    return (
      <DialogPortal data-slot="dialog-portal" container={portalContainer ?? undefined}>
        {hasPortalContainerProp ? (
          <div
            data-slot="dialog-overlay"
            data-state="open"
            {...overlayProps}
            className={cn(
              "animate-in fade-in-0 inset-0 z-200 bg-black/50",
              position === "absolute" ? "absolute" : "fixed",
              overlayProps?.className
            )}
          />
        ) : (
          <DialogOverlay
            disableScrollLock={disableScrollLock}
            position={position}
            {...overlayProps}
          />
        )}
        <DialogPrimitive.Content
          ref={ref}
          data-slot="dialog-content"
          // Radix calls this before it moves focus in, so the active element is still the opener.
          onOpenAutoFocus={(event) => {
            const { activeElement } = document;
            openerRef.current =
              activeElement instanceof HTMLElement && activeElement !== document.body
                ? activeElement
                : null;
            onOpenAutoFocus?.(event);
          }}
          onCloseAutoFocus={(event) => {
            onCloseAutoFocus?.(event);
            const opener = openerRef.current;
            openerRef.current = null;
            // A caller that places focus itself keeps the last word, and an opener that has been
            // unmounted since is left to Radix.
            if (event.defaultPrevented || !opener?.isConnected) return;
            event.preventDefault();
            opener.focus();
          }}
          className={cn(
            "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 top-[50%] left-[50%] z-210 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-2xl border p-6 shadow-lg duration-200 sm:max-w-lg",
            position === "absolute" ? "absolute" : "fixed",
            className
          )}
          {...props}
        >
          {children}
          {showCloseButton && <DialogClose className="absolute top-4 right-4" />}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = "DialogContent";

const DialogHeader = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-slot="dialog-header"
        className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
        {...props}
      />
    );
  }
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-slot="dialog-footer"
        className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
        {...props}
      />
    );
  }
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Title
      ref={ref}
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  );
});
DialogTitle.displayName = "DialogTitle";

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Description
      ref={ref}
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
});
DialogDescription.displayName = "DialogDescription";

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
